import { apiClient } from './client';

export interface ProductFeatureDefinition {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'text' | 'select';
  defaultValue?: unknown;
  description?: string | null;
  adminVisible?: boolean;
  nullable?: boolean;
  nullableLabel?: string | null;
  min?: number;
  max?: number;
  options?: Array<{ value: string; label: string }>;
}

export interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationMeta;
}

export interface AdminOverview {
  summary: {
    totalUsers: number;
    paidUsers: number;
    blockedUsers: number;
    products: number;
    plans: number;
    subscriptions: number;
    activeSubscriptions: number;
    pendingSubscriptions: number;
  };
  products: Array<{
    id: number;
    code: string;
    name: string;
    description?: string | null;
    status: string;
    planCount: number;
    subscriptionCount: number;
    activeSubscriptionCount: number;
    revokedSubscriptionCount: number;
    cancelledSubscriptionCount: number;
    plans: Array<{
      id: number;
      code: string;
      name: string;
      activeCount: number;
      revokedCount: number;
      cancelledCount: number;
    }>;
    insights: {
      activeCreators: number;
      totalCommentsAnalysed: number;
      hoursSaved: number;
      contentIdeasGenerated: number;
    };
    createdAt: string;
    updatedAt: string;
  }>;
}

export interface AnalysisQueueBucketStats {
  waiting: number;
  active: number;
  delayed: number;
  completed: number | null;
  failed: number | null;
  paused?: number;
}

export interface AnalysisQueueStats {
  provider: 'memory' | 'bullmq';
  fallbackProvider?: 'memory';
  mode: 'memory' | 'bullmq';
  enabled?: boolean;
  paid: AnalysisQueueBucketStats;
  free: AnalysisQueueBucketStats;
  limits: {
    totalConcurrency: number;
    paidConcurrency: number;
    freeConcurrency: number;
    paidBurst: number;
  };
  totals: {
    waiting: number;
    active: number;
    delayed?: number;
    completed?: number;
    failed?: number;
  };
}

export type ChannelAnalyticsQueueStats = AnalysisQueueStats;

export interface AdminQueuesOverview {
  analysis: AnalysisQueueStats;
  scripts: AnalysisQueueStats;
  channelAnalytics: ChannelAnalyticsQueueStats;
}

export interface AdminUserRow {
  id: number;
  email: string;
  name?: string | null;
  plan: string;
  isAdmin: boolean;
  analysesCount: number;
  hasAnalyses?: boolean;
  channelAnalysesCount?: number;
  hasConnectedChannels?: boolean;
  connectedChannels?: Array<{
    id?: number | null;
    provider: string;
    title?: string | null;
    connectedAt?: string | null;
  }>;
  youtubeConnected?: boolean;
  youtubeChannelTitle?: string | null;
  socialConnected?: boolean;
  instagramUsername?: string | null;
  subscriptionStatus?: string | null;
  subscriptionCurrentEnd?: string | null;
  subscriptionProductCode?: string | null;
  subscriptionPlanName?: string | null;
  productCodes: string[];
  isBlocked: boolean;
  blockedReason?: string | null;
  blockedAt?: string | null;
  emailVerified: boolean;
  emailVerifiedAt?: string | null;
  termsAcceptedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserDetail {
  user: {
    id: number;
    email: string;
    name?: string | null;
    plan: string;
    isAdmin: boolean;
    isBlocked: boolean;
    blockedReason?: string | null;
    blockedAt?: string | null;
    emailVerified: boolean;
    emailVerifiedAt?: string | null;
    termsAcceptedAt?: string | null;
    createdAt: string;
    updatedAt: string;
    channelConnections?: Array<{
      id?: number | null;
      provider: string;
      title?: string | null;
      connectedAt?: string | null;
    }>;
    hasConnectedChannels?: boolean;
    youtubeConnected?: boolean;
    youtubeChannelTitle?: string | null;
    socialConnected?: boolean;
    instagramUsername?: string | null;
    channelAnalysesCount?: number;
    currentSubscription?: {
      status: string;
      currentStart?: string | null;
      currentEnd?: string | null;
      cancelAtCycleEnd: boolean;
      providerSubscriptionId?: string | null;
      product?: { code: string; name: string } | null;
      plan?: { code: string; name: string } | null;
    } | null;
  };
  recentAnalyses: Array<{
    sessionId: string;
    url: string;
    title?: string | null;
    platform?: string | null;
    status: string;
    commentsCount: number;
    createdAt: string;
    updatedAt: string;
    completedAt?: string | null;
  }>;
  recentChannelAnalyses?: Array<{
    id: number;
    status: string;
    createdAt: string;
    completedAt?: string | null;
  }>;
  subscriptions: Array<{
    id: number;
    product?: { code: string; name: string } | null;
    plan?: { code: string; name: string } | null;
    status: string;
    providerSubscriptionId?: string | null;
    currentStart?: string | null;
    currentEnd?: string | null;
    cancelAtCycleEnd: boolean;
    createdAt: string;
  }>;
}

export interface AdminProductRow {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  featureSchema: ProductFeatureDefinition[];
  status: string;
  planCount: number;
  subscriptionCount: number;
  activeSubscriptionCount: number;
  revokedSubscriptionCount: number;
  cancelledSubscriptionCount: number;
  plans: Array<{
    id: number;
    code: string;
    name: string;
    activeCount: number;
    revokedCount: number;
    cancelledCount: number;
  }>;
  planCodes: string[];
  subscriberEmails: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminPlanRow {
  id: number;
  product?: { code: string; name: string } | null;
  code: string;
  name: string;
  description?: string | null;
  amount: number;
  currency: string;
  intervalUnit: string;
  intervalCount: number;
  razorpayPlanId?: string | null;
  status: string;
  isDefault: boolean;
  isPublic: boolean;
  isPopular: boolean;
  showOnLanding: boolean;
  billingType: 'free' | 'recurring';
  features: Record<string, unknown>;
  featureSchema: ProductFeatureDefinition[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminSubscriptionRow {
  id: number | string;
  userId: number;
  email: string;
  name?: string | null;
  product?: { code: string; name: string } | null;
  plan?: { code: string; name: string; intervalUnit?: string | null } | null;
  status: string;
  provider: string;
  providerSubscriptionId?: string | null;
  providerPlanId?: string | null;
  currentStart?: string | null;
  currentEnd?: string | null;
  cancelAtCycleEnd: boolean;
  analysesUsedThisMonth?: number;
  maxAnalysesPerMonth?: number | null;
  reanalysesUsedThisMonth?: number;
  maxReanalysesPerMonth?: number | null;
  createdAt: string;
  updatedAt: string;
  isSyntheticFreeTier?: boolean;
}

export const getAdminOverview = () =>
  apiClient.get<{ success: true; data: AdminOverview }>('/api/admin/overview');

export const getAnalysisQueueStats = () =>
  apiClient.get<{ success: true; data: AnalysisQueueStats }>('/api/admin/queues/analysis');

export const getAdminQueuesOverview = () =>
  apiClient.get<{ success: true; data: AdminQueuesOverview }>('/api/admin/queues');

export const getAdminUsers = (params?: { q?: string; page?: number; pageSize?: number; blocked?: boolean }) =>
  apiClient.get<{ success: true; data: PaginatedResult<AdminUserRow> }>('/api/admin/users', { params });

export const getAdminUserDetail = (userId: number) =>
  apiClient.get<{ success: true; data: AdminUserDetail }>(`/api/admin/users/${userId}`);

export const getAdminProducts = (params?: { q?: string; page?: number; pageSize?: number }) =>
  apiClient.get<{ success: true; data: PaginatedResult<AdminProductRow> }>('/api/admin/products/overview', { params });

export const createAdminProduct = (
  payload: { code: string; name: string; description?: string; status?: string; featureSchema?: ProductFeatureDefinition[] },
) =>
  apiClient.post<{ success: true; data: AdminProductRow }>('/api/admin/products', payload);

export const updateAdminProduct = (
  productId: number,
  payload: { name?: string; description?: string; status?: string; featureSchema?: ProductFeatureDefinition[] },
) =>
  apiClient.patch<{ success: true; data: AdminProductRow }>(`/api/admin/products/${productId}`, payload);

export const deleteAdminProduct = (productId: number) =>
  apiClient.delete<{ success: true; data: { id: number; deleted?: boolean; fallbackAction?: string } }>(
    `/api/admin/products/${productId}`,
  );

export const getAdminPlans = (params?: { productCode?: string; q?: string; page?: number; pageSize?: number }) =>
  apiClient.get<{ success: true; data: PaginatedResult<AdminPlanRow> }>('/api/admin/plans', { params });

export const updateAdminPlan = (
  planId: number,
  payload: Partial<Pick<AdminPlanRow, 'name' | 'description' | 'amount' | 'currency' | 'status' | 'isDefault' | 'isPublic' | 'isPopular' | 'showOnLanding'>> & {
    intervalUnit?: string;
    intervalCount?: number;
    features?: AdminPlanRow['features'];
  },
) =>
  apiClient.patch<{ success: true; data: AdminPlanRow }>(`/api/admin/plans/${planId}`, payload);

export const deleteAdminPlan = (planId: number) =>
  apiClient.delete<{ success: true; data: { id: number; deleted?: boolean; fallbackAction?: string; activeSubscriptionCount?: number } }>(
    `/api/admin/plans/${planId}`,
  );

export const syncAdminPlanToRazorpay = (planId: number) =>
  apiClient.post<{ success: true; data: AdminPlanRow }>(`/api/admin/plans/${planId}/sync-razorpay`, {});


export const getAdminSubscriptions = (params?: { productCode?: string; q?: string; page?: number; pageSize?: number }) =>
  apiClient.get<{ success: true; data: PaginatedResult<AdminSubscriptionRow> }>('/api/admin/user-subscriptions', { params });

export const assignAdminSubscription = (
  payload: {
    userId: number;
    planId: number;
    status?: string;
    currentStart?: string;
    currentEnd?: string;
    provider?: string;
    force?: boolean;
  },
) =>
  apiClient.post('/api/admin/user-subscriptions', payload);

export const revokeAdminSubscription = (subscriptionId: number, cancelAtCycleEnd?: boolean) =>
  apiClient.post(`/api/admin/user-subscriptions/${subscriptionId}/revoke`, { cancelAtCycleEnd });

export const resetAdminMonthlyQuota = (
  userId: number,
  productCode: string,
  type: 'analyses' | 'reanalyses' | 'both' = 'analyses',
) =>
  apiClient.post(`/api/admin/users/${userId}/products/${productCode}/reset-monthly-quota`, { type });

export const blockAdminUser = (userId: number, reason?: string) =>
  apiClient.post<{ success: true; data: { message: string } }>(`/api/admin/users/${userId}/block`, { reason });

export const unblockAdminUser = (userId: number) =>
  apiClient.post<{ success: true; data: { message: string } }>(`/api/admin/users/${userId}/unblock`, {});

export const deleteAdminUser = (userId: number, reason?: string) =>
  apiClient.delete<{ success: true; data: { message: string; deletedUserId: number; deletedEmail: string; reason: string | null } }>(
    `/api/admin/users/${userId}`,
    { data: { reason: reason || undefined } },
  );

export const verifyAdminUserEmail = (userId: number) =>
  apiClient.post<{ success: true; data: { message: string; emailVerified: boolean; emailVerifiedAt: string; termsAcceptedAt?: string | null } }>(
    `/api/admin/users/${userId}/verify-email`,
    {},
  );

export interface ConsentTrendDay {
  date: string;
  accepted: number;
  rejected: number;
}

export interface ConsentStats {
  total: number;
  granularTotal: number;
  analyticsAccepted: number;
  analyticsRate: number | null;
  marketingAccepted: number;
  marketingRate: number | null;
  legacyTotal: number;
  legacyAccepted: number;
  legacyRate: number | null;
  // Authenticated user preferences (profile page toggle)
  userTotal: number;
  userOptIn: number;
  userExplicitOptOut: number;
  userOptInRate: number | null;
  trend: ConsentTrendDay[];
}

export const getConsentStats = () =>
  apiClient.get<{ success: true; data: ConsentStats }>('/api/admin/cookie-consent/stats');

// ── YouTube Quota ─────────────────────────────────────────────────────────────

export interface YoutubeQuotaHistoryEntry {
  date: string;
  units: number;
}

export interface YoutubeQuotaStats {
  date: string;
  unitsUsed: number;
  dailyLimit: number;
  percentUsed: number;
  remaining: number;
  isWarning: boolean;
  isCritical: boolean;
  nextReset: string;
  history: YoutubeQuotaHistoryEntry[];
}

export const getYoutubeQuotaStats = () =>
  apiClient.get<{ success: true; data: YoutubeQuotaStats }>('/api/admin/youtube-quota');

// ── Security ──────────────────────────────────────────────────────────────────

export interface SecurityViolationUser {
  id: number;
  email: string;
  name: string | null;
  isBlocked: boolean;
}

export interface SecurityViolation {
  ip: string;
  hits: number;
  firstSeen: string | null;
  lastSeen: string | null;
  limiters: string[];
  users: SecurityViolationUser[];
}

export interface SecurityBlockedUser {
  id: number;
  email: string;
  name: string | null;
  blockedReason: string | null;
  blockedAt: string | null;
  knownIps: string[];
}

export interface SecurityBlockedIp {
  ip: string;
  blockedAt: string;
  reason: string;
  hits: number;
}

export interface SecurityOverview {
  violations: SecurityViolation[];
  blockedUsers: SecurityBlockedUser[];
  blockedIps: SecurityBlockedIp[];
  summary: {
    totalViolatingIps: number;
    totalBlockedUsers: number;
    totalBlockedIps: number;
    totalHits: number;
    autoBlockThreshold: number;
  };
}

export const getAdminSecurity = (params?: { from?: string; to?: string }) =>
  apiClient.get<{ success: true; data: SecurityOverview }>('/api/admin/security', { params });

export const clearAdminSecurityLogs = () =>
  apiClient.delete<{ success: true; data: { message: string; clearedKeys: number } }>('/api/admin/security/logs');

export const blockAdminIp = (ip: string, reason?: string) =>
  apiClient.post<{ success: true; data: { message: string } }>('/api/admin/security/blocked-ips', { ip, reason });

export const unblockAdminIp = (ip: string) =>
  apiClient.delete<{ success: true; data: { message: string } }>(`/api/admin/security/blocked-ips/${encodeURIComponent(ip)}`);

export const getAdminSecurityExportUrl = (params?: { from?: string; to?: string }) => {
  const base = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
  const qs   = params?.from || params?.to
    ? `?${new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString()}`
    : '';
  return `${base}/api/admin/security/export${qs}`;
};

// ── Offers ────────────────────────────────────────────────────────────────────

export interface AdminOfferRow {
  id: number;
  productCode: string;
  code: string;
  title: string;
  description: string | null;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  discountLabel: string;
  bannerText: string;
  showBanner: boolean;
  maxRedemptions: number | null;
  currentRedemptions: number;
  validFrom: string;
  validUntil: string | null;
  razorpayOfferId: string | null;
  razorpayOfferIds: Record<string, string[]>;
  status: 'active' | 'inactive' | 'expired';
  createdAt: string;
  updatedAt: string;
}

export interface CreateOfferInput {
  productCode: string;
  code: string;
  title: string;
  description?: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  maxRedemptions?: number | null;
  validFrom: string;
  validUntil?: string | null;
  showBanner?: boolean;
  bannerText?: string;
  razorpayOfferId?: string | null;
}

export interface UpdateOfferInput {
  title?: string;
  description?: string | null;
  discountType?: 'percentage' | 'fixed';
  discountValue?: number;
  maxRedemptions?: number | null;
  validFrom?: string;
  validUntil?: string | null;
  showBanner?: boolean;
  bannerText?: string | null;
  status?: 'active' | 'inactive' | 'expired';
  razorpayOfferId?: string | null;
}

export const listAdminOffers = (productCode?: string) =>
  apiClient.get<{ success: true; data: { items: AdminOfferRow[]; pagination: PaginationMeta } }>('/api/admin/offers', {
    params: productCode ? { productCode } : undefined,
  });

export const createAdminOffer = (input: CreateOfferInput) =>
  apiClient.post<{ success: true; data: AdminOfferRow }>('/api/admin/offers', input);

export const updateAdminOffer = (offerId: number, input: UpdateOfferInput) =>
  apiClient.patch<{ success: true; data: AdminOfferRow }>(`/api/admin/offers/${offerId}`, input);

export const syncAdminOfferToRazorpay = (offerId: number, razorpayOfferId: string, method?: string) =>
  apiClient.post<{ success: true; data: AdminOfferRow }>(`/api/admin/offers/${offerId}/sync-razorpay`, { razorpayOfferId, method });

export const deleteAdminOffer = (offerId: number) =>
  apiClient.delete<{ success: true; data: { id: number; deleted: boolean } }>(`/api/admin/offers/${offerId}`);

// ── Subscription Health ────────────────────────────────────────────────────────

export interface JobState {
  lastRunAt:      string | null;
  lastDurationMs: number | null;
  lastResult:     Record<string, number> | null;
  lastError:      string | null;
  runCount:       number;
  schedule:       string | null;
  scheduleLabel:  string | null;
}

export interface SubscriptionHealthData {
  serverStartedAt: string;
  jobs: {
    expireEndedSubscriptions: JobState;
    purgeStaleCheckouts:      JobState;
    syncActiveSubscriptions:  JobState;
    fixGhostedUsers:          JobState;
  };
  subscriptions: {
    byStatus:              Record<string, number>;
    expiringSoon24h:       number;
    expiringSoon48h:       number;
    recentlyCompleted24h:  number;
    staleCheckouts:        number;
    ghostUsers:            number;
  };
  plans: Array<{
    code:         string;
    name:         string;
    intervalUnit: string;
    active:       number;
    authenticated:number;
    cancelled:    number;
    halted:       number;
  }>;
  generatedAt: string;
}

export const getSubscriptionHealth = () =>
  apiClient.get<{ success: true; data: SubscriptionHealthData }>('/api/admin/subscription-health');

// ── Health Metrics ─────────────────────────────────────────────────────────────

export interface HealthMetrics {
  collectedAt: string;
  process: {
    pid: number;
    nodeVersion: string;
    uptimeSeconds: number;
    memory: {
      rss: string;
      heapTotal: string;
      heapUsed: string;
      external: string;
      arrayBuffers: string;
      heapUsedPercent: number;
      heapSizeLimit: string;
      heapSizeLimitBytes: number;
      heapUsedVsLimit: number;
    };
    cpu: { userMs: number; systemMs: number };
  };
  os: {
    platform: string;
    arch: string;
    cpus: number;
    loadAvg: { '1m': number; '5m': number; '15m': number };
    memory: { total: string; totalBytes: number; free: string; freePercent: number };
  };
  redis: {
    status: string;
    version: string | null;
    mode: string;
    role: string | null;
    uptimeSeconds: number;
    memory: {
      used: string | null;
      usedBytes: number;
      peak: string | null;
      rss: string | null;
      maxmemory: string | null;
      fragmentationRatio: number | null;
    };
    clients: { connected: number; blocked: number };
    keyspace: { totalKeys: number; hits: number; misses: number; cacheHitRate: number | null };
    throughput: { commandsProcessed: number; opsPerSec: number; inputMB: number; outputMB: number };
    persistence: { rdbEnabled: boolean; rdbLastSaveAt: string | null; rdbPendingChanges: number; aofEnabled: boolean };
    replication: { connectedSlaves: number };
  };
  database: {
    status: string;
    provider: string;
    latencyMs: number;
    size: string;
    sizeBytes: number;
    connections: { total: number; active: number; idle: number; waiting: number };
    transactions: { committed: number; rolledBack: number };
    bufferHitRate: number;
    tuples: { returned: number; fetched: number; inserted: number; updated: number; deleted: number };
    anomalies: {
      deadlocks: number;
      conflicts: number;
      tempFiles: number;
      tempBytes: string;
      tempFilesTotal: number;
      tempFileDelta: {
        sinceLastCheck: number | null;
        intervalMinutes: number | null;
        perMinute: number | null;
      };
    };
  };
}

export const getHealthMetrics = () =>
  apiClient.get<{ success: true; data: HealthMetrics }>('/api/health/metrics');

export type MaintenanceAction =
  | 'clear_redis_cache'
  | 'analyze_database'
  | 'reset_connections'
  | 'clear_failed_analyses'
  | 'clear_old_analysis_json';

export const runMaintenanceAction = (action: MaintenanceAction) =>
  apiClient.post<{ success: true; data: { action: string; deleted?: number; updated?: number; message: string } }>(
    '/api/admin/maintenance',
    { action },
  );

// ─── Cooldown Accounts ───────────────────────────────────────────────────────

export interface CooldownAccount {
  id: number;
  email: string;
  originalUserId: number | null;
  deletedAt: string;
  cooldownUntil: string;
}

export const listCooldownAccounts = (params?: { page?: number; pageSize?: number; search?: string }) =>
  apiClient.get<{ success: true; data: PaginatedResult<CooldownAccount> }>('/api/admin/cooldown-accounts', { params });

export const liftCooldown = (id: number) =>
  apiClient.delete<{ success: true; data: { lifted: boolean; email: string } }>(`/api/admin/cooldown-accounts/${id}`);


// ─── IP Blocks ────────────────────────────────────────────────────────────────

export interface IpBlock {
  ipHash: string;
  count: number;
  firstSeen: string;
  lastSeen: string;
}

export const listIpBlocks = (params?: { page?: number; pageSize?: number }) =>
  apiClient.get<{ success: true; data: PaginatedResult<IpBlock> }>('/api/admin/ip-blocks', { params });

export const liftIpBlock = (ipHash: string) =>
  apiClient.delete<{ success: true; data: { lifted: boolean; ipHash: string; deletedRows: number } }>(`/api/admin/ip-blocks/${ipHash}`);
